import { useLayoutEffect, useRef } from 'react'

/**
 * Sticky top bar — the brand-anchor of the player app.
 *
 * Two rows per design.md §5:
 *
 *   Row 1: POLF mark · event name
 *   Row 2: ● LIVE/POKER status pill · player name · hcp
 *
 * Dark green-900 background carries the sport-product identity across every
 * screen. The status pill flips LIVE → POKER on phase change so a glance at
 * the top says "what mode am I in".
 *
 * The bar publishes its rendered height to `--player-topbar-h` on
 * documentElement so pages can stack a second sticky surface (e.g. the
 * Score page's hole navigator) directly under it without hard-coding a
 * pixel offset.
 */
type Props = {
  eventName: string
  playerName: string
  hcp: number | null | undefined
  /**
   * Short mode label rendered as the row-1 eyebrow ("POLF", "Golf",
   * "Scramble", "Best ball"). Drops the misleading hardcoded "POLF" on
   * non-poker events.
   */
  modeLabel: string
  /**
   * Status drives the pill copy:
   *   - GOLF phase + STARTED event  → "● LIVE"
   *   - POKER phase + RUNNING poker → "● POKER"
   *   - any FINISHED/CLOSED state   → "FERDIG" (no pulse)
   *
   * Passed in so the shell controls semantics — TopBar only renders.
   */
  statusLabel: 'LIVE' | 'POKER' | 'FERDIG' | 'PAUSE'
  /**
   * Ambient offline flag. When true, a thin warning strip is appended to the
   * (sticky) bar so pages stacked under `--player-topbar-h` shift down with it.
   */
  offline?: boolean
}

const TopBar = ({
  eventName,
  playerName,
  hcp,
  modeLabel,
  statusLabel,
  offline = false,
}: Props) => {
  const showPulse = statusLabel === 'LIVE' || statusLabel === 'POKER'
  const pillBg =
    statusLabel === 'LIVE'
      ? 'bg-flag/15 text-flag'
      : statusLabel === 'POKER'
      ? 'bg-gold/20 text-gold'
      : statusLabel === 'PAUSE'
      ? 'bg-warn/25 text-warn'
      : 'bg-ink-500/30 text-ink-300'

  const ref = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const publish = () => {
      document.documentElement.style.setProperty(
        '--player-topbar-h',
        `${el.offsetHeight}px`
      )
    }
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <header
      ref={ref}
      className="sticky top-0 z-40 bg-green-900 text-paper border-b border-green-800"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Row 1 — brand mark + event */}
      <div className="flex items-center gap-3 px-4 py-2">
        <div
          aria-hidden
          className="w-7 h-7 rounded-full bg-cream-card flex items-center justify-center shrink-0 p-0.5"
        >
          <img
            src="/polf-mark.png"
            alt=""
            className="w-full h-full object-contain"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-bold text-green-100/70 truncate"
            style={{
              letterSpacing: 'var(--ls-caps)',
              textTransform: 'uppercase',
            }}
          >
            {modeLabel}
          </div>
          <div className="text-sm font-bold text-paper truncate">
            {eventName}
          </div>
        </div>
      </div>
      {/* Row 2 — status pill + meta. The "Mid-runde / Bordet ditt"
          subtitle was dropped: if you can see the score buttons, you
          know you're mid-round. Status pill already carries phase. */}
      <div className="flex items-center gap-2 px-4 pb-2">
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pillBg}`}
          aria-label={`Status: ${statusLabel}`}
        >
          {showPulse ? (
            <span
              className="w-1.5 h-1.5 rounded-full bg-current"
              style={{ animation: 'polf-pulse 1.6s ease-in-out infinite' }}
            />
          ) : null}
          {statusLabel}
        </span>
        <span className="flex-1" />
        <span className="text-[11px] text-green-100/80 truncate max-w-[50%]">
          {playerName}
        </span>
        {hcp !== null && hcp !== undefined ? (
          <span className="text-[11px] text-paper font-mono num font-semibold">
            HCP {hcp}
          </span>
        ) : null}
      </div>
      {/* Offline strip — ambient context, not a gate. Score entry stays live;
          this just explains why a save might not stick right now. */}
      {offline ? (
        <div
          role="status"
          className="flex items-center justify-center gap-1.5 bg-warn text-green-900 px-4 py-1 text-[11px] font-semibold"
        >
          <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-current" />
          Frakoblet – endringer lagres ikke før du er på nett igjen
        </div>
      ) : null}
    </header>
  )
}

export default TopBar
